import { useCallback, useEffect, useReducer, useRef } from "react";
import { getAssessmentData, useLocale } from "../i18n";
import type {
  AiDiagnosis,
  LocalProgress,
  ResultsData,
} from "../types/assessment";
import { useLocalPersistence } from "./useLocalPersistence";
import { useSync } from "./useSync";

// --- Question groups (single source of truth) ---
export const questionGroups = [
  { num: 1, start: 0, end: 4, size: 5 },
  { num: 2, start: 5, end: 10, size: 6 },
  { num: 3, start: 11, end: 16, size: 6 },
  { num: 4, start: 17, end: 22, size: 6 },
] as const;

// --- State ---
type Phase =
  | "welcome"
  | "questions"
  | "emailCapture"
  | "companyCapture"
  | "themeComplete"
  | "loading"
  | "paused"
  | "results";

interface State {
  phase: Phase;
  currentQuestion: number;
  currentGroupIndex: number;
  answers: Record<number, string>;
  email: string | null;
  sessionId: string | null;
  companyName: string | null;
  companySuggestion: string | null;
  companyConfirmationRequired: boolean;
  resultsData: ResultsData | null;
  aiDiagnosis: AiDiagnosis | null;
}

const initialState: State = {
  phase: "welcome",
  currentQuestion: 0,
  currentGroupIndex: 0,
  answers: {},
  email: null,
  sessionId: null,
  companyName: null,
  companySuggestion: null,
  companyConfirmationRequired: false,
  resultsData: null,
  aiDiagnosis: null,
};

// --- Actions ---
type Action =
  | { type: "START" }
  | { type: "RESTORE"; payload: Partial<State> }
  | { type: "SELECT_ANSWER"; questionIndex: number; optionId: string }
  | { type: "NEXT_QUESTION" }
  | { type: "PREV_QUESTION" }
  | { type: "SHOW_EMAIL_CAPTURE" }
  | { type: "SHOW_COMPANY_CAPTURE" }
  | { type: "SHOW_THEME_COMPLETE" }
  | { type: "SHOW_LOADING" }
  | { type: "SHOW_RESULTS"; resultsData?: ResultsData }
  | {
      type: "SET_EMAIL";
      email: string;
      sessionId?: string;
      companyName?: string | null;
      companySuggestion?: string | null;
      companyConfirmationRequired?: boolean;
    }
  | { type: "CONTINUE_AFTER_EMAIL" }
  | { type: "CONTINUE_AFTER_THEME" }
  | { type: "PAUSE" }
  | { type: "RESUME_FROM_PAUSE" }
  | { type: "BACK_FROM_INTERMEDIATE" }
  | { type: "RESET" };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "START":
      return { ...initialState, phase: "questions" };

    case "RESTORE": {
      const restored = { ...state, ...action.payload };
      // If we paused right after finishing group 1 (before email capture), go there
      const g0 = questionGroups[0];
      const atGroupBoundary =
        restored.currentGroupIndex === 0 && restored.currentQuestion === g0.end;
      return {
        ...restored,
        phase: atGroupBoundary ? "emailCapture" : "questions",
      };
    }

    case "SELECT_ANSWER":
      return {
        ...state,
        answers: { ...state.answers, [action.questionIndex]: action.optionId },
      };

    case "NEXT_QUESTION": {
      const group = questionGroups[state.currentGroupIndex];
      // End of a group?
      if (state.currentQuestion === group.end) {
        if (state.currentGroupIndex === 0) {
          return { ...state, phase: "emailCapture" };
        }
        if (state.currentGroupIndex < questionGroups.length - 1) {
          return { ...state, phase: "themeComplete" };
        }
        return { ...state, phase: "results" };
      }
      return { ...state, currentQuestion: state.currentQuestion + 1 };
    }

    case "PREV_QUESTION":
      if (state.currentQuestion > 0) {
        const prevQuestion = state.currentQuestion - 1;
        const prevGroup = questionGroups.find(
          (g) => prevQuestion >= g.start && prevQuestion <= g.end,
        );
        const nextGroupIndex = prevGroup
          ? prevGroup.num - 1
          : state.currentGroupIndex;
        return {
          ...state,
          currentQuestion: prevQuestion,
          currentGroupIndex: nextGroupIndex,
        };
      }
      return state;

    case "SHOW_EMAIL_CAPTURE":
      return { ...state, phase: "emailCapture" };

    case "SHOW_COMPANY_CAPTURE":
      return { ...state, phase: "companyCapture" };

    case "SHOW_THEME_COMPLETE":
      return { ...state, phase: "themeComplete" };

    case "SHOW_LOADING":
      return { ...state, phase: "loading" };

    case "SHOW_RESULTS":
      return {
        ...state,
        phase: "results",
        resultsData: action.resultsData ?? state.resultsData,
        aiDiagnosis: action.resultsData?.aiDiagnosis ?? state.aiDiagnosis,
      };

    case "SET_EMAIL":
      return {
        ...state,
        email: action.email,
        sessionId: action.sessionId ?? state.sessionId,
        companyName: action.companyName ?? state.companyName,
        companySuggestion: action.companySuggestion ?? state.companySuggestion,
        companyConfirmationRequired:
          action.companyConfirmationRequired ??
          state.companyConfirmationRequired,
      };

    case "CONTINUE_AFTER_EMAIL": {
      const nextGroup = questionGroups[1];
      return {
        ...state,
        phase: "questions",
        currentGroupIndex: 1,
        currentQuestion: nextGroup.start,
      };
    }

    case "CONTINUE_AFTER_THEME": {
      const nextIdx = state.currentGroupIndex + 1;
      const nextGroup = questionGroups[nextIdx];
      return {
        ...state,
        phase: "questions",
        currentGroupIndex: nextIdx,
        currentQuestion: nextGroup.start,
      };
    }

    case "BACK_FROM_INTERMEDIATE": {
      // Return to the last question of the current group
      const group = questionGroups[state.currentGroupIndex];
      return {
        ...state,
        phase: "questions",
        currentQuestion: group.end,
      };
    }

    case "PAUSE":
      return { ...state, phase: "paused" };

    case "RESUME_FROM_PAUSE":
      return { ...state, phase: "emailCapture" };

    case "RESET":
      return initialState;

    default:
      return state;
  }
}

// --- Hook ---
export function useAssessment() {
  const locale = useLocale();
  const assessmentQuestions = getAssessmentData(locale);
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    save,
    load,
    clear,
    setPendingFinish,
    getPendingFinish,
    clearPendingFinish,
  } = useLocalPersistence();
  const {
    createSession,
    syncAnswers,
    resumeByEmail,
    updateCompany,
    fetchSession,
    fetchResults,
    pauseSession,
  } = useSync();
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  // Restore from localStorage or ?email= query param on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get("email");
    if (emailParam) {
      // Remove the param from the URL without a page reload
      const url = new URL(window.location.href);
      url.searchParams.delete("email");
      window.history.replaceState({}, "", url.toString());
      // Try backend resume — falls through to localStorage if not found
      resumeByEmail(emailParam).then((session) => {
        if (session) {
          dispatch({
            type: "RESTORE",
            payload: {
              answers: session.answers,
              currentQuestion: session.currentQuestion,
              currentGroupIndex: session.currentGroup,
              sessionId: session.id,
              email: session.email,
              companyName: session.companyName,
              companySuggestion: session.companySuggestion,
              companyConfirmationRequired: false,
            },
          });
          return;
        }
      });
      return;
    }

    const saved = load();
    if (saved && Object.keys(saved.answers).length > 0) {
      dispatch({
        type: "RESTORE",
        payload: {
          answers: saved.answers,
          currentQuestion: saved.currentQuestion,
          currentGroupIndex: saved.currentGroupIndex,
          sessionId: saved.sessionId,
          email: saved.email,
          companyName: saved.companyName ?? null,
          companySuggestion: saved.companySuggestion ?? null,
          companyConfirmationRequired:
            saved.companyConfirmationRequired ?? false,
        },
      });
    }
  }, [load, resumeByEmail]);

  // Persist to localStorage on every state change (when in progress)
  useEffect(() => {
    if (state.phase === "welcome" || state.phase === "results") return;
    const progress: LocalProgress = {
      answers: state.answers,
      currentQuestion: state.currentQuestion,
      currentGroupIndex: state.currentGroupIndex,
      sessionId: state.sessionId,
      email: state.email,
      companyName: state.companyName,
      companySuggestion: state.companySuggestion,
      companyConfirmationRequired: state.companyConfirmationRequired,
    };
    save(progress);
  }, [
    state.answers,
    state.currentQuestion,
    state.currentGroupIndex,
    state.sessionId,
    state.email,
    state.companyName,
    state.companySuggestion,
    state.companyConfirmationRequired,
    state.phase,
    save,
  ]);

  // When coming back online, complete a finish that was deferred due to offline state
  useEffect(() => {
    const handleOnline = async () => {
      const pendingSessionId = getPendingFinish();
      if (!pendingSessionId) return;
      clearPendingFinish();

      dispatch({ type: "SHOW_LOADING" });

      const results = await fetchResults(pendingSessionId);
      dispatch({ type: "SHOW_RESULTS", resultsData: results ?? undefined });
    };

    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [getPendingFinish, clearPendingFinish, fetchResults]);

  // Debounced sync to backend
  const scheduleSync = useCallback(() => {
    if (!state.sessionId) return;
    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    syncTimeoutRef.current = setTimeout(() => {
      syncAnswers({
        answers: state.answers,
        currentQuestion: state.currentQuestion,
        currentGroupIndex: state.currentGroupIndex,
        sessionId: state.sessionId,
        email: state.email,
      });
    }, 3500);
  }, [
    state.sessionId,
    state.answers,
    state.currentQuestion,
    state.currentGroupIndex,
    state.email,
    syncAnswers,
  ]);

  // --- Public actions ---

  const start = useCallback(() => {
    clear();
    dispatch({ type: "START" });
  }, [clear]);

  const selectAnswer = useCallback(
    (optionId: string) => {
      dispatch({
        type: "SELECT_ANSWER",
        questionIndex: state.currentQuestion,
        optionId,
      });
      scheduleSync();
    },
    [state.currentQuestion, scheduleSync],
  );

  const next = useCallback(() => {
    dispatch({ type: "NEXT_QUESTION" });
  }, []);

  const previous = useCallback(() => {
    dispatch({ type: "PREV_QUESTION" });
  }, []);

  const saveEmailAndContinue = useCallback(
    async (email: string) => {
      dispatch({ type: "SET_EMAIL", email });

      // Create backend session
      const session = await createSession({
        answers: state.answers,
        currentQuestion: state.currentQuestion,
        currentGroupIndex: state.currentGroupIndex,
        sessionId: null,
        email,
      });

      if (session) {
        dispatch({
          type: "SET_EMAIL",
          email,
          sessionId: session.id,
          companyName: session.companyName,
          companySuggestion: session.companySuggestion,
          companyConfirmationRequired: session.companyConfirmationRequired,
        });
      }

      dispatch({ type: "CONTINUE_AFTER_EMAIL" });
    },
    [
      state.answers,
      state.currentQuestion,
      state.currentGroupIndex,
      createSession,
    ],
  );

  const confirmCompanyAndContinue = useCallback(
    async (companyName: string) => {
      if (!state.sessionId || !state.email) {
        if (state.phase === "companyCapture") {
          dispatch({ type: "SHOW_RESULTS" });
        } else {
          dispatch({ type: "CONTINUE_AFTER_EMAIL" });
        }
        return;
      }

      const session = await updateCompany(state.sessionId, companyName);
      if (session) {
        dispatch({
          type: "SET_EMAIL",
          email: state.email,
          sessionId: session.id,
          companyName: session.companyName,
          companySuggestion: session.companySuggestion,
          companyConfirmationRequired: session.companyConfirmationRequired,
        });
      }

      if (state.phase === "companyCapture") {
        const results = await fetchResults(state.sessionId);
        dispatch({ type: "SHOW_RESULTS", resultsData: results ?? undefined });
        clear();
        return;
      }

      dispatch({ type: "CONTINUE_AFTER_EMAIL" });
    },
    [
      state.sessionId,
      state.email,
      state.phase,
      updateCompany,
      fetchResults,
      clear,
    ],
  );

  const pauseForLater = useCallback(
    async (email: string) => {
      const resolvedEmail = email || state.email || "";
      if (resolvedEmail) {
        dispatch({ type: "SET_EMAIL", email: resolvedEmail });
      }

      // Create backend session if none exists yet (user pauses before saving email)
      let resolvedSessionId = state.sessionId;
      if (!resolvedSessionId && resolvedEmail) {
        const session = await createSession({
          answers: state.answers,
          currentQuestion: state.currentQuestion,
          currentGroupIndex: state.currentGroupIndex,
          sessionId: null,
          email: resolvedEmail,
        });
        if (session) {
          resolvedSessionId = session.id;
          dispatch({
            type: "SET_EMAIL",
            email: resolvedEmail,
            sessionId: session.id,
          });
        }
      }

      save({
        answers: state.answers,
        currentQuestion: state.currentQuestion,
        currentGroupIndex: state.currentGroupIndex,
        sessionId: resolvedSessionId,
        email: resolvedEmail,
        companyName: state.companyName,
        companySuggestion: state.companySuggestion,
        companyConfirmationRequired: state.companyConfirmationRequired,
      });

      if (resolvedSessionId && resolvedEmail) {
        const base = window.location.origin + window.location.pathname;
        const resumeLink = `${base}?email=${encodeURIComponent(resolvedEmail)}`;
        await pauseSession(resolvedSessionId, resumeLink);
      }

      dispatch({ type: "PAUSE" });
    },
    [state, save, createSession, pauseSession],
  );

  const continueFromPause = useCallback(() => {
    dispatch({ type: "RESUME_FROM_PAUSE" });
  }, []);

  const continueAfterTheme = useCallback(() => {
    dispatch({ type: "CONTINUE_AFTER_THEME" });
    scheduleSync();
  }, [scheduleSync]);

  const backFromIntermediate = useCallback(() => {
    dispatch({ type: "BACK_FROM_INTERMEDIATE" });
  }, []);

  const finishAndGetResults = useCallback(async () => {
    if (!navigator.onLine) {
      // Save a flag so the online listener can complete the flow later
      if (state.sessionId) setPendingFinish(state.sessionId);
      dispatch({ type: "SHOW_RESULTS" });
      clear();
      return;
    }

    dispatch({ type: "SHOW_LOADING" });

    if (state.sessionId) {
      await syncAnswers({
        answers: state.answers,
        currentQuestion: state.currentQuestion,
        currentGroupIndex: state.currentGroupIndex,
        sessionId: state.sessionId,
        email: state.email,
      });

      const latestSession = await fetchSession(state.sessionId);
      if (latestSession) {
        dispatch({
          type: "SET_EMAIL",
          email: latestSession.email ?? state.email ?? "",
          sessionId: latestSession.id,
          companyName: latestSession.companyName,
          companySuggestion: latestSession.companySuggestion,
          companyConfirmationRequired:
            latestSession.companyConfirmationRequired,
        });

        if (latestSession.companyConfirmationRequired) {
          dispatch({ type: "SHOW_COMPANY_CAPTURE" });
          return;
        }
      }

      const results = await fetchResults(state.sessionId);
      dispatch({ type: "SHOW_RESULTS", resultsData: results ?? undefined });
      clear();
      return;
    }

    dispatch({ type: "SHOW_RESULTS" });
    clear();
  }, [
    state.sessionId,
    state.answers,
    state.currentQuestion,
    state.currentGroupIndex,
    state.email,
    syncAnswers,
    fetchSession,
    fetchResults,
    clear,
    setPendingFinish,
  ]);

  const tryResumeByEmail = useCallback(
    async (email: string): Promise<boolean> => {
      const session = await resumeByEmail(email);
      if (session) {
        dispatch({
          type: "RESTORE",
          payload: {
            answers: session.answers,
            currentQuestion: session.currentQuestion,
            currentGroupIndex: session.currentGroup,
            sessionId: session.id,
            email: session.email,
            companyName: session.companyName,
            companySuggestion: session.companySuggestion,
            companyConfirmationRequired: session.companyConfirmationRequired,
          },
        });
        return true;
      }
      return false;
    },
    [resumeByEmail],
  );

  const retryDiagnosis = useCallback(async () => {
    if (!state.sessionId || !navigator.onLine) return;
    dispatch({ type: "SHOW_LOADING" });
    const results = await fetchResults(state.sessionId);
    dispatch({ type: "SHOW_RESULTS", resultsData: results ?? undefined });
  }, [state.sessionId, fetchResults]);

  // --- Derived values ---
  const currentGroup = questionGroups[state.currentGroupIndex];
  const currentQuestionData = assessmentQuestions[state.currentQuestion];
  const isLastQuestionInGroup = questionGroups.some(
    (g) => state.currentQuestion === g.end,
  );
  const answeredCount = currentGroup
    ? state.currentQuestion - currentGroup.start + 1
    : 0;
  const totalQuestionsInTheme = currentGroup ? currentGroup.size : 0;

  const calculateGroupScore = (groupIndex: number): number => {
    const group = questionGroups[groupIndex];
    let totalLevels = 0;
    for (let i = group.start; i <= group.end; i++) {
      const answerId = state.answers[i];
      if (answerId) {
        const selectedOption = assessmentQuestions[i].options.find(
          (opt) => opt.id === answerId,
        );
        if (selectedOption) {
          totalLevels += selectedOption.level;
        }
      }
    }
    return totalLevels;
  };

  const calculateTotalScore = (): number => {
    let totalLevels = 0;
    for (const [index, q] of assessmentQuestions.entries()) {
      const answerId = state.answers[index];
      if (answerId) {
        const selectedOption = q.options.find((opt) => opt.id === answerId);
        if (selectedOption) {
          totalLevels += selectedOption.level;
        }
      }
    }
    return totalLevels;
  };

  const maxScorePerQuestion = 4;
  const calculateAllThemeScores = () => ({
    strategie:
      (calculateGroupScore(0) /
        (questionGroups[0].size * maxScorePerQuestion)) *
      maxScorePerQuestion,
    marque:
      (calculateGroupScore(1) /
        (questionGroups[1].size * maxScorePerQuestion)) *
      maxScorePerQuestion,
    contenu:
      (calculateGroupScore(2) /
        (questionGroups[2].size * maxScorePerQuestion)) *
      maxScorePerQuestion,
    ia:
      (calculateGroupScore(3) /
        (questionGroups[3].size * maxScorePerQuestion)) *
      maxScorePerQuestion,
  });

  return {
    // State
    phase: state.phase,
    currentQuestion: state.currentQuestion,
    currentGroupIndex: state.currentGroupIndex,
    answers: state.answers,
    email: state.email,
    sessionId: state.sessionId,
    companyName: state.companyName,
    companySuggestion: state.companySuggestion,
    companyConfirmationRequired: state.companyConfirmationRequired,
    resultsData: state.resultsData,
    aiDiagnosis: state.aiDiagnosis,

    // Derived
    currentGroup,
    currentQuestionData,
    isLastQuestionInGroup,
    answeredCount,
    totalQuestionsInTheme,
    totalQuestions: assessmentQuestions.length,

    // Scoring
    calculateGroupScore,
    calculateTotalScore,
    calculateAllThemeScores,
    maxScorePerQuestion,

    // Actions
    start,
    selectAnswer,
    next,
    previous,
    saveEmailAndContinue,
    confirmCompanyAndContinue,
    pauseForLater,
    continueFromPause,
    continueAfterTheme,
    backFromIntermediate,
    finishAndGetResults,
    tryResumeByEmail,
    retryDiagnosis,
    restart: start,
  };
}
